/**
 * Decrypt modal component for DEX reader workflow.
 * Allows readers to decrypt payloads by providing their private key
 * (via file upload or text paste), with automatic download of decrypted content.
 */

import { renderKeyInputTabs, initKeyInputTabs } from './key-input-tabs.js';
import { decryptPayload, validatePrivateKey } from '../../api/crypto.js';
import { downloadDecrypted } from '../../utils/download.js';
import { showToast } from './toast.js';

// Module-level state
let currentPayload = null;
let privateKey = null;
let rememberKey = false;
let keyInputState = {
  activeTab: 'file',
  file: null,
  fileName: null,
  fileError: null,
  textContent: '',
  textError: null
};

/**
 * Render the decrypt modal into a container.
 * @param {HTMLElement} container - Container element to render into
 * @param {Object} state - Modal state
 * @param {Object|null} [state.payload] - Payload to decrypt
 * @param {string|null} [state.rememberedKey] - Remembered private key
 * @param {Object} [state.keyInputState] - Key input tabs state
 * @param {string} [state.status='idle'] - Current status: 'idle' | 'decrypting' | 'complete'
 * @param {string|null} [state.error] - Error message
 */
export function renderDecryptModal(container, state = {}) {
  const {
    payload = null,
    rememberedKey = null,
    keyInputState: stateKeyInput = keyInputState,
    status = 'idle',
    error = null
  } = state;

  if (!payload) {
    container.innerHTML = '';
    return;
  }

  // Parse payload PK for display
  const pkDisplay = payload.pk || 'Unknown';

  // Status display
  let statusHtml = '';
  if (status === 'decrypting') {
    statusHtml = `
      <div class="flex items-center justify-center py-2">
        <svg class="animate-spin h-5 w-5 text-blue-600 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="text-gray-600 text-sm">Decrypting...</span>
      </div>
    `;
  }

  // Error display
  const errorHtml = error
    ? `<div class="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
        <p class="text-sm text-red-800">${escapeHtml(error)}</p>
      </div>`
    : '';

  const hasRememberedKey = !!rememberedKey;

  container.innerHTML = `
    <div
      id="decrypt-modal-backdrop"
      class="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-20"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[85vh] overflow-y-auto">
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-gray-900">Decrypt Payload</h2>
          <button
            id="decrypt-close-btn"
            class="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <div class="px-6 py-4">
          ${errorHtml}

          <!-- Payload Info -->
          <div class="bg-gray-50 border border-gray-200 rounded-md p-3 mb-4">
            <p class="text-sm text-gray-700">
              <span class="font-medium">Payload ID:</span>
              <span class="font-mono text-xs ml-1">${escapeHtml(pkDisplay)}</span>
            </p>
            ${payload.status ? `
              <p class="text-sm text-gray-700 mt-1">
                <span class="font-medium">Status:</span>
                <span class="ml-1">${escapeHtml(payload.status)}</span>
              </p>
            ` : ''}
          </div>

          <!-- Private Key Input -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Your Private Key
            </label>
            ${hasRememberedKey ? `
              <div class="bg-green-50 border border-green-200 rounded-md p-3 mb-2">
                <p class="text-sm text-green-800">Using remembered private key</p>
                <button
                  type="button"
                  id="decrypt-clear-remembered"
                  class="text-xs text-green-600 hover:text-green-800 underline mt-1"
                >
                  Use a different key
                </button>
              </div>
            ` : `
              <div id="decrypt-key-input-container"></div>
            `}
          </div>

          <!-- Remember Key Checkbox -->
          <div class="mb-4">
            <label class="flex items-center text-sm text-gray-700">
              <input
                type="checkbox"
                id="decrypt-remember-key"
                class="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                ${status === 'decrypting' ? 'disabled' : ''}
              />
              Remember private key for this session
            </label>
            <p class="text-gray-400 text-xs ml-6">Key stored in memory only, cleared on page reload</p>
          </div>

          ${statusHtml}
        </div>

        <div class="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            id="decrypt-cancel-btn"
            class="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            ${status === 'decrypting' ? 'disabled' : ''}
          >
            Cancel
          </button>
          <button
            id="decrypt-submit-btn"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            ${status === 'decrypting' ? 'disabled' : ''}
          >
            ${status === 'decrypting' ? 'Decrypting...' : 'Decrypt & Download'}
          </button>
        </div>
      </div>
    </div>
  `;

  // Render key input tabs if no remembered key
  if (!hasRememberedKey) {
    const keyInputContainer = container.querySelector('#decrypt-key-input-container');
    if (keyInputContainer) {
      renderKeyInputTabs(keyInputContainer, stateKeyInput);
    }
  }
}

/**
 * Initialize the decrypt modal with event handlers.
 * @param {HTMLElement} container - Container element
 * @param {Object} callbacks - Callback functions
 * @param {Function} [callbacks.onClose] - Called when modal is closed
 * @param {Function} [callbacks.onDecryptComplete] - Called after successful decrypt: ({ payload, rememberKey }) => void
 * @param {Function} [callbacks.onRememberChange] - Called when remember checkbox changes: (remember) => void
 * @returns {Function} Cleanup function
 */
export function initDecryptModal(container, callbacks = {}) {
  const { onClose, onDecryptComplete, onRememberChange } = callbacks;

  const cleanupFunctions = [];

  // Close handlers
  const closeBtn = container.querySelector('#decrypt-close-btn');
  const cancelBtn = container.querySelector('#decrypt-cancel-btn');
  const backdrop = container.querySelector('#decrypt-modal-backdrop');

  function handleClose() {
    if (onClose) {
      onClose();
    }
  }

  function handleBackdropClick(e) {
    if (e.target === backdrop) {
      handleClose();
    }
  }

  if (closeBtn) {
    closeBtn.addEventListener('click', handleClose);
    cleanupFunctions.push(() => closeBtn.removeEventListener('click', handleClose));
  }

  if (cancelBtn) {
    cancelBtn.addEventListener('click', handleClose);
    cleanupFunctions.push(() => cancelBtn.removeEventListener('click', handleClose));
  }

  if (backdrop) {
    backdrop.addEventListener('click', handleBackdropClick);
    cleanupFunctions.push(() => backdrop.removeEventListener('click', handleBackdropClick));
  }

  // Remember checkbox
  const rememberCheckbox = container.querySelector('#decrypt-remember-key');
  if (rememberCheckbox) {
    function handleRememberChange(e) {
      rememberKey = e.target.checked;
      if (onRememberChange) {
        onRememberChange(rememberKey);
      }
    }
    rememberCheckbox.addEventListener('change', handleRememberChange);
    cleanupFunctions.push(() => rememberCheckbox.removeEventListener('change', handleRememberChange));
  }

  // Clear remembered key button
  const clearRememberedBtn = container.querySelector('#decrypt-clear-remembered');
  if (clearRememberedBtn) {
    function handleClearRemembered() {
      privateKey = null;
      keyInputState = { ...keyInputState, file: null, fileName: null, fileError: null, textContent: '', textError: null };
      renderDecryptModal(container, {
        payload: currentPayload,
        rememberedKey: null,
        keyInputState,
        status: 'idle',
        error: null
      });
      initDecryptModal(container, callbacks);
    }
    clearRememberedBtn.addEventListener('click', handleClearRemembered);
    cleanupFunctions.push(() => clearRememberedBtn.removeEventListener('click', handleClearRemembered));
  }

  // Key input tabs (only if container exists - i.e., no remembered key)
  const keyInputContainer = container.querySelector('#decrypt-key-input-container');
  if (keyInputContainer) {
    // Helper to re-render just the key input tabs and re-wire handlers
    function rerenderKeyTabs() {
      renderKeyInputTabs(keyInputContainer, keyInputState);
      initKeyInputTabs(keyInputContainer, keyInputCallbacks, keyInputState);
    }

    const keyInputCallbacks = {
      onTabChange: (tab) => {
        keyInputState = { ...keyInputState, activeTab: tab };
        rerenderKeyTabs();
      },
      onFileChange: (content, error, file) => {
        if (error) {
          keyInputState = { ...keyInputState, fileError: error, file: null, fileName: null };
          privateKey = null;
        } else {
          keyInputState = { ...keyInputState, file, fileName: file?.name, fileError: null };
          privateKey = content;
        }
        rerenderKeyTabs();
      },
      onFileClear: () => {
        keyInputState = { ...keyInputState, file: null, fileName: null, fileError: null };
        privateKey = null;
        rerenderKeyTabs();
      },
      onTextChange: (text) => {
        keyInputState = { ...keyInputState, textContent: text };
        privateKey = text.trim();
      }
    };

    const keyInputCleanup = initKeyInputTabs(keyInputContainer, keyInputCallbacks, keyInputState);
    cleanupFunctions.push(keyInputCleanup);
  }

  // Decrypt button
  const submitBtn = container.querySelector('#decrypt-submit-btn');
  if (submitBtn) {
    async function handleDecrypt() {
      // Read private key from textarea if in text mode
      const textArea = container.querySelector('#key-text-content');
      if (textArea) {
        privateKey = textArea.value.trim();
      }

      // Validate private key
      if (!privateKey) {
        renderDecryptModal(container, {
          payload: currentPayload,
          rememberedKey: null,
          keyInputState,
          status: 'idle',
          error: 'Private key is required'
        });
        initDecryptModal(container, callbacks);
        return;
      }

      // Validate private key format
      const validation = validatePrivateKey(privateKey);
      if (!validation.valid) {
        renderDecryptModal(container, {
          payload: currentPayload,
          rememberedKey: null,
          keyInputState,
          status: 'idle',
          error: `Invalid private key: ${validation.error}`
        });
        initDecryptModal(container, callbacks);
        showToast(`Invalid private key: ${validation.error}`, 'error');
        return;
      }

      // Set status to decrypting
      renderDecryptModal(container, {
        payload: currentPayload,
        rememberedKey: privateKey,
        keyInputState,
        status: 'decrypting',
        error: null
      });
      initDecryptModal(container, callbacks);

      try {
        // Decrypt the payload (public key derived from private key automatically)
        const plaintext = await decryptPayload(
          currentPayload.payload,
          validation.key
        );

        // Generate filename from pk or default
        const filename = currentPayload.pk
          ? `decrypted_${currentPayload.pk.substring(0, 20)}.bin`
          : 'decrypted.bin';

        // Download the decrypted content
        downloadDecrypted(plaintext, filename);

        // Show success toast
        showToast('Decrypted and downloaded!', 'success');

        // Call completion callback
        if (onDecryptComplete) {
          onDecryptComplete({
            payload: currentPayload,
            rememberKey: rememberCheckbox?.checked || false
          });
        }

        // Close modal after successful decrypt
        handleClose();
      } catch (err) {
        const errorMessage = err.message || 'Decryption failed';
        renderDecryptModal(container, {
          payload: currentPayload,
          rememberedKey: null,
          keyInputState,
          status: 'idle',
          error: errorMessage
        });
        initDecryptModal(container, callbacks);
        showToast(errorMessage, 'error');
      }
    }

    submitBtn.addEventListener('click', handleDecrypt);
    cleanupFunctions.push(() => submitBtn.removeEventListener('click', handleDecrypt));
  }

  // Return combined cleanup function
  return function cleanup() {
    cleanupFunctions.forEach((fn) => fn());
  };
}

/**
 * Show the decrypt modal for a specific payload.
 * @param {HTMLElement} container - Container element
 * @param {Object} payload - Payload to decrypt
 * @param {string|null} [rememberedKey] - Pre-fill remembered private key
 */
export function showDecryptModal(container, payload, rememberedKey = null) {
  // Set module state
  currentPayload = payload;
  privateKey = rememberedKey || null;
  rememberKey = false;
  keyInputState = {
    activeTab: 'file',
    file: null,
    fileName: null,
    fileError: null,
    textContent: '',
    textError: null
  };

  // Render only - caller should call initDecryptModal with callbacks
  renderDecryptModal(container, {
    payload,
    rememberedKey,
    keyInputState,
    status: 'idle',
    error: null
  });
}

/**
 * Hide the decrypt modal.
 * @param {HTMLElement} container - Container element
 */
export function hideDecryptModal(container) {
  container.innerHTML = '';
  currentPayload = null;
}

/**
 * Escape HTML special characters to prevent XSS.
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
